import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Lock, Link2, Check } from "lucide-react";
import { toast } from "sonner";
import { Panel, Skeleton, EmptyState } from "../components/ui";
import Disclaimer from "../components/Disclaimer";
import PostBlocks from "../components/PostBlocks";
import CommentThread from "../components/CommentThread";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { shortDate } from "../lib/format";
import Avatar from "../components/Avatar";
import Reactions from "../components/Reactions";
import { PostTickerBar } from "../components/PostCharts";
import useSeo from "../lib/useSeo";
import VerifiedBadge from "../components/VerifiedBadge";
import { useTranslation } from "react-i18next";

export default function PostDetail() {
  const { t } = useTranslation();
  const { slug } = useParams();
  const { user, isPaid } = useAuth();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState([]);
  // Deep-linking to a comment is CommentThread's job now: it has to expand a collapsed
  // reply before anything can scroll to it.
  const [share, setShare] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(() => {
    api.post(slug).then((r) => setPost(r.data)).catch(() => setPost(null)).finally(() => setLoading(false));
  }, [slug]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (post && !post.locked) api.postComments(slug).then((r) => setComments(r.data)).catch(() => {});
  }, [post, slug]);

  // The author gets their referral link right here. Posts are shared far more often
  // than profiles, so this is where the link is actually useful.
  useEffect(() => {
    if (post && user && user.id === post.investor.investor_id) {
      api.partnerMe().then((r) => setShare(r.data)).catch(() => {});
    }
  }, [post, user]);

  useSeo({
    title: post?.title,
    description: post?.excerpt,
    // Only fully public posts are indexable; a paywalled post would otherwise be
    // indexed on an excerpt it can't deliver.
    noindex: post ? post.visibility !== "public" : true,
    jsonLd: post ? {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description: post.excerpt,
      datePublished: post.published_at,
      dateModified: post.updated_at,
      author: { "@type": "Person", name: post.investor?.display_name },
      isAccessibleForFree: post.visibility === "public",
    } : null,
  });

  // Handed to CommentThread, which owns the input and the reply boxes. `parentId` is
  // null for a top-level comment.
  const submit = async (text, parentId) => {
    if (!text.trim()) return;
    try {
      const r = await api.addPostComment(slug, { body: text, parent_id: parentId || null });
      toast.success(r.data?.status === "approved" ? t("comments.posted") : t("investors.commentPending"));
      api.postComments(slug).then((res) => setComments(res.data)).catch(() => {});
    } catch (e) { toast.error(e.response?.data?.detail || t("dialogs.failed")); }
  };

  if (loading) return <div className="max-w-3xl mx-auto p-6 space-y-4"><Skeleton className="h-10" /><Skeleton className="h-64" /></div>;
  if (!post) return <EmptyState testid="post-404" title={t("posts.notFound")} description={t("posts.notFoundDesc")} />;

  return (
    <article className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-6">
      <Link to={`/investors/${post.investor.investor_id}`}
            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-brand-600">
        <ArrowLeft size={16} /> {post.investor.display_name}
      </Link>

      <header>
        <h1 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tighter">{post.title}</h1>
        {/* A proper byline, not a grey line of metadata. Who wrote a post is the first
            thing a reader wants to know and the reason they might follow anyone — it
            was previously indistinguishable from the date. Links to the profile. */}
        <div className="flex items-center gap-3 mt-4" data-testid="post-byline">
          <Link to={`/investors/${post.investor.investor_id}`} className="shrink-0">
            <Avatar name={post.investor.display_name} size={38} />
          </Link>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <Link to={`/investors/${post.investor.investor_id}`}
                    className="font-medium text-slate-900 hover:text-brand-600 truncate">
                {post.investor.display_name}
              </Link>
              {post.investor.verified && <VerifiedBadge size={14} />}
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-400">
              {post.published_at && <span>{shortDate(post.published_at)}</span>}
              {post.visibility === "subscribers" && (
                <span className="pill bg-brand-50 text-brand-600 text-[10px]">{t("posts.membersOnly")}</span>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Tags are shown even on a locked post: knowing what it is about is the
          argument for subscribing, and a ticker list gives nothing away. */}
      <PostTickerBar tickers={post.tickers} />

      {post.locked ? (
        <>
          <p className="text-slate-600 leading-relaxed">{post.excerpt}</p>
          {post.chart_count > 0 && (
            <p className="text-xs text-slate-400" data-testid="post-locked-charts">
              {t("postCharts.lockedCount", { count: post.chart_count })}
            </p>
          )}
          <Panel className="card p-6 text-center bg-brand-50/50 border-brand-100" testid="post-paywall">
            <Lock size={22} className="text-brand-600 mx-auto" />
            <h2 className="font-heading font-semibold text-lg mt-2">{t("posts.lockedTitle")}</h2>
            <p className="text-sm text-slate-600 mt-1">{t("posts.lockedBody")}</p>
            <Link to={user ? "/billing" : "/login"} className="btn-primary mt-4 inline-flex" data-testid="post-upgrade">
              {user ? t("billing.upgrade") : t("leaderboard.cta")}
            </Link>
          </Panel>
        </>
      ) : (
        /* Prose, charts and images in the order the author arranged them. Charts used
           to be appended after the entire body, so "as the chart below shows" pointed
           at whichever of them happened to be next. Everything authored is sanitised
           server-side and rendered through RichText. */
        <PostBlocks blocks={post.blocks} />
      )}

      {!post.locked && <Reactions targetType="post" targetId={post.id} className="pt-2" />}

      {share?.link && (
        <Panel className="card p-4 bg-brand-50/50 border-brand-100" testid="post-share-link">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-start gap-2.5 min-w-0 flex-1">
              <Link2 size={16} className="text-brand-600 shrink-0 mt-0.5" />
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-900">{t("posts.shareTitle")}</div>
                <p className="text-xs text-slate-600 mt-0.5">{t("posts.shareBody")}</p>
              </div>
            </div>
            <button
              className="btn-primary shrink-0 justify-center"
              data-testid="copy-post-referral"
              onClick={() => {
                navigator.clipboard?.writeText(`${window.location.origin}/posts/${slug}?ref=${share.handle}`);
                setCopied(true); setTimeout(() => setCopied(false), 2000);
                toast.success(t("partner.copied"));
              }}
            >
              {copied ? <Check size={15} /> : <Link2 size={15} />} {t("posts.copyShareLink")}
            </button>
          </div>
        </Panel>
      )}

      {!post.locked && (
        <Panel className="card p-5" testid="post-comments">
          <h2 className="font-heading font-semibold tracking-tight mb-3">{t("investors.comments")}</h2>
          {/* The server opens a *public* post to any signed-in account — discussion is
              what makes it worth finding — and only gates members-only posts behind the
              subscription. The box used to require `isPaid` either way, hiding a control
              from people the API would have accepted. */}
          <CommentThread
            comments={comments}
            canComment={!!user && (post.visibility === "public" || isPaid)}
            onAdd={submit}
            idPrefix="pc"
          />
        </Panel>
      )}

      <Disclaimer />
    </article>
  );
}
